import { Link } from 'react-router-dom';
import { formatDisplayName } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/store/authStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MainLayout } from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useDashboardStats,
  StatsCards,
  RecentDocuments,
  QuickUpload,
} from '@/features/dashboard';
import { FileCheck } from 'lucide-react';

export default function UserDashboard() {
  const { user } = useAuthStore();
  const { stats, isLoading, refreshStats } = useDashboardStats();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 sm:py-8">
        <div className="space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {getGreeting()}, {user?.firstName || user?.userId}!
              </h1>
              <p className="text-muted-foreground">
                Here's an overview of your account activity
              </p>
            </div>
            <Button asChild size="lg" className="shrink-0">
              <Link to="/file-tax">
                <FileCheck className="h-5 w-5 mr-2" />
                File your taxes
              </Link>
            </Button>
          </div>

          {/* Stats Cards */}
          <StatsCards stats={stats} isLoading={isLoading} />

          {/* Main Content Grid */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left Column - Quick Upload & Recent Documents */}
            <div className="lg:col-span-2 space-y-6">
              <QuickUpload onUploadComplete={refreshStats} />
              <RecentDocuments documents={stats.recentDocuments} isLoading={isLoading} />
            </div>

            {/* Right Column - Account Info */}
            <div className="space-y-6">
              {/* Account Info Card */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Account Information</CardTitle>
                  <CardDescription>Your account details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        User ID
                      </p>
                      <p className="font-medium text-foreground">{user?.userId}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Name
                      </p>
                      <p className="font-medium text-foreground">{formatDisplayName(user)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Role
                      </p>
                      <Badge variant="secondary" className="capitalize">
                        {user?.role?.replace('role_', '').toLowerCase()}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Account ID
                      </p>
                      <p className="font-mono text-xs text-muted-foreground break-all">
                        {user?._id}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Tips Card */}
              <Card className="bg-accent/30 border-accent">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-accent-foreground">Quick Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-accent-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Upload documents using the quick upload area above</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>View all your documents in the Documents section</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Keep your profile information up to date</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
